import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHead } from '@/components/admin/page-head';
import { useConfirm, useToast } from '@/components/ui/feedback';
import {
  useDeleteMedia,
  useMediaLibrary,
  useMissingVisual,
  useUpdateMedia,
  useUploadMedia,
} from '@/hooks/use-catalog';
import { extractErrorMessage } from '@/hooks/use-auth-api';
import type { MediaLibraryItem } from '@/lib/catalog/catalog-api';

const PAGE_SIZE = 24;

function MediaCard({
  m,
  onDelete,
  onSaveAlt,
}: {
  m: MediaLibraryItem;
  onDelete: (m: MediaLibraryItem) => void;
  onSaveAlt: (m: MediaLibraryItem, alt: string) => void;
}) {
  const [alt, setAlt] = useState(m.altText ?? '');
  return (
    <div style={{ border: '1px solid var(--a-border,#eee)', borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
      <div style={{ aspectRatio: '1 / 1', background: 'var(--a-bg,#f6f6f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {m.kind === 'image' ? (
          <img src={m.url} alt={m.altText ?? m.filename} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: 32, opacity: 0.4 }}>{m.kind === 'video' ? '🎬' : '📄'}</span>
        )}
      </div>
      <div style={{ padding: '6px 8px' }}>
        <div style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={m.filename}>
          {m.filename}
        </div>
        <input
          className="aform-i aform-i-xs"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          onBlur={() => { if ((m.altText ?? '') !== alt) onSaveAlt(m, alt); }}
          placeholder="Texte alternatif…"
          title="Texte alternatif (accessibilité, SEO, recherche) — enregistré à la sortie du champ"
          style={{ fontSize: 11, marginTop: 4, width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
          <span className={`tag ${m.usageCount > 0 ? 'success' : 'neutral'} no-dot`} style={{ fontSize: 10 }}>
            {m.usageCount > 0 ? `Utilisée ×${m.usageCount}` : 'Non utilisée'}
          </span>
          {m.usageCount === 0 && (
            <button className="btn btn-ghost btn-sm" title="Supprimer" onClick={() => onDelete(m)}>✕</button>
          )}
        </div>
      </div>
    </div>
  );
}

function LibraryTab() {
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [usage, setUsage] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useMediaLibrary({
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    kind: (kind || undefined) as 'image' | 'video' | 'document' | undefined,
    usage: (usage || undefined) as 'used' | 'unused' | undefined,
  });
  const uploadMut = useUploadMedia();
  const deleteMut = useDeleteMedia();
  const updateMut = useUpdateMedia();
  const toast = useToast();
  const confirm = useConfirm();

  const saveAlt = async (m: MediaLibraryItem, alt: string) => {
    try {
      await updateMut.mutateAsync({ id: m.id, altText: alt });
      toast.success('Texte alternatif enregistré.');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Enregistrement impossible'));
    }
  };

  const onUpload = async (files: FileList | null) => {
    if (!files?.length) return;
    try {
      for (const f of Array.from(files)) await uploadMut.mutateAsync({ file: f });
      toast.success(`${files.length} fichier(s) importé(s).`);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Import impossible'));
    }
  };

  const remove = async (m: MediaLibraryItem) => {
    const ok = await confirm({ title: `Supprimer « ${m.filename} » ?`, message: 'Le fichier sera définitivement retiré de la médiathèque.', confirmLabel: 'Supprimer', tone: 'danger' });
    if (!ok) return;
    try {
      await deleteMut.mutateAsync(m.id);
      toast.success('Média supprimé.');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Suppression impossible'));
    }
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="card">
      <div className="card-h" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="aform-i" placeholder="Rechercher (nom, texte alt)…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ maxWidth: 260 }} />
        <select className="aform-i" value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }}>
          <option value="">Tous types</option>
          <option value="image">Images</option>
          <option value="video">Vidéos</option>
          <option value="document">Documents</option>
        </select>
        <select className="aform-i" value={usage} onChange={(e) => { setUsage(e.target.value); setPage(1); }}>
          <option value="">Tous usages</option>
          <option value="used">Utilisées</option>
          <option value="unused">Non utilisées</option>
        </select>
        <span className="muted" style={{ fontSize: 12 }}>{total} média(s)</span>
        <div style={{ flex: 1 }} />
        <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
          + Importer
          <input type="file" multiple accept="image/*,video/*,application/pdf" style={{ display: 'none' }} onChange={(e) => void onUpload(e.target.files)} />
        </label>
      </div>
      <div className="card-b">
        {isLoading && <p className="muted">Chargement…</p>}
        {!isLoading && items.length === 0 && <p className="muted" style={{ fontSize: 13 }}>Aucun média.</p>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
          {items.map((m) => <MediaCard key={m.id} m={m} onDelete={remove} onSaveAlt={saveAlt} />)}
        </div>
        {pages > 1 && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, alignItems: 'center' }}>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Précédent</button>
            <span className="muted" style={{ fontSize: 12 }}>Page {page} / {pages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Suivant →</button>
          </div>
        )}
      </div>
    </div>
  );
}

function MissingVisualTab() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const { data, isLoading } = useMissingVisual({ page, pageSize: PAGE_SIZE, search: search || undefined });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="card">
      <div className="card-h" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <input className="aform-i" placeholder="Rechercher (SKU, titre)…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ maxWidth: 260 }} />
        <span className="tag warning no-dot">{total} produit(s) sans visuel</span>
      </div>
      <div className="card-b" style={{ padding: 0 }}>
        <table className="table" style={{ width: '100%' }}>
          <thead>
            <tr><th>SKU</th><th>Produit</th><th>Statut</th><th style={{ width: 100 }}></th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={4} className="muted" style={{ padding: 20, textAlign: 'center' }}>Chargement…</td></tr>}
            {!isLoading && items.length === 0 && <tr><td colSpan={4} className="muted" style={{ padding: 20, textAlign: 'center' }}>Tous les produits ont au moins un visuel 🎉</td></tr>}
            {items.map((p) => (
              <tr key={p.id}>
                <td className="mono" style={{ fontSize: 12 }}>{p.sku}</td>
                <td style={{ fontWeight: 600 }}>{p.title}</td>
                <td><span className="tag neutral no-dot">{p.status}</span></td>
                <td>
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/catalogue/${p.id}/modifier`)}>Ajouter</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pages > 1 && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', padding: 14, alignItems: 'center' }}>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Précédent</button>
            <span className="muted" style={{ fontSize: 12 }}>Page {page} / {pages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Suivant →</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminMediaLibraryPage() {
  const [tab, setTab] = useState<'library' | 'missing'>('library');
  const { data: missing } = useMissingVisual({ pageSize: 1 });

  return (
    <>
      <PageHead
        title="Bibliothèque d'images"
        sub="Tous les visuels du catalogue au même endroit — recherche, réutilisation, produits sans visuel."
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className={`btn ${tab === 'library' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('library')}>
          Médiathèque
        </button>
        <button className={`btn ${tab === 'missing' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('missing')}>
          Produits sans visuel{missing ? ` (${missing.total})` : ''}
        </button>
      </div>
      {tab === 'library' ? <LibraryTab /> : <MissingVisualTab />}
    </>
  );
}
